/**
 * @file index.tsx
 *
 * @fileoverview Renders all field items from a the FormWidget component.
 * The main purpose of this component is to add the appropriate component
 * based on the type of field.
 */
import { EntityField } from '~/jobiqo/models/entity';
import React from 'react';
import dynamic from 'next/dynamic';
import { useFormContext } from 'react-hook-form';
import ErrorFocus from '~/jobiqo/components/Form/ErrorFocus';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import StringMultipleWidget from '~/jobiqo/components/Form/Widgets/StringMultiple';
import { EntityFieldRules } from '~/jobiqo/models/config';
import AdditionalFieldItemWidgetsSlot from '~/jobiqo/components/Slots/AdditionalFieldItemWidgetsSlot';
import DateSingleWidget from '~/jobiqo/components/Form/Widgets/Date/Single';
import DateRangeWidget from '~/jobiqo/components/Form/Widgets/Date/Range';
import DateRangeMultipleWidget from '~/jobiqo/components/Form/Widgets/Date/RangeMultiple';

const ListStringWidget = dynamic(
  import('~/jobiqo/components/Form/Widgets/ListString'),
);
const StringWidget = dynamic(import('~/jobiqo/components/Form/Widgets/String'));
const StringLongWidget = dynamic(
  import('~/jobiqo/components/Form/Widgets/StringLong'),
);
const TextLongWidget = dynamic(
  import('~/jobiqo/components/Form/Widgets/TextLong'),
  {
    ssr: false,
  },
);
const BooleanWidget = dynamic(
  import('~/jobiqo/components/Form/Widgets/Boolean'),
);
const FileWidget = dynamic(import('~/jobiqo/components/Form/Widgets/File'));
const ImageFileWidget = dynamic(
  import('~/jobiqo/components/Form/Widgets/ImageFile'),
);
const TaxonomyReferenceMultiple = dynamic(
  import(
    '~/jobiqo/components/Form/Widgets/TaxonomyReference/TaxonomyReferenceMultiple'
  ),
);
const TaxonomyReferenceSingle = dynamic(
  import(
    '~/jobiqo/components/Form/Widgets/TaxonomyReference/TaxonomyReferenceSingle'
  ),
);
const AddressWidget = dynamic(
  import('~/jobiqo/components/Form/Widgets/Address'),
);
const TermLevelWidget = dynamic(
  import('~/jobiqo/components/Form/Widgets/TermLevel'),
);
const TermLevelWithDateWidget = dynamic(
  import('~/jobiqo/components/Form/Widgets/TermLevelWithDate'),
);
const ImageUploadCroppingWidget = dynamic(
  import('~/jobiqo/components/Form/Widgets/ImageUploadCropping'),
);
const SalaryRangeWidget = dynamic(
  import('~/jobiqo/components/Form/Widgets/SalaryRange'),
);

// Props needed by the form component.
interface Props {
  /**
   * The name of the entity to be displayed (node, user, profile).
   */
  name: string;
  /**
   * The bundle of the entity to be displayed (job_per_templae, job_per_link).
   */
  bundle?: string;
  /**
   * The field list to render.
   */
  fields: EntityField[];
  /**
   * The field rules to check if the field is required, hidden or disabled.
   */
  fieldRules?: EntityFieldRules;
  /**
   * Render prop for custom elements.
   */
  overrides?: {
    [key: string]: ({ field, setFieldValue, values, formState }) => void;
  };
}

/**
 * Get the target type for an entity reference field.
 *
 * @param {EntityField} field
 *   The field for which we want to get the target from.
 *
 * @returns {string}
 *   The target type.
 */
const getTargetType = (field: EntityField): string | null => {
  if (field.settings) {
    const setting = field.settings.filter((i) => {
      return i.key === 'target_type';
    });
    if (setting.length > 0) {
      return setting[0].value;
    }
  }

  return null;
};

export const FormItemsWidget = ({
  name,
  bundle,
  fields,
  fieldRules = {},
  overrides,
}: Props) => {
  const { setValue, getValues, formState } = useFormContext();
  const values = getValues();

  return (
    <>
      <ErrorFocus />
      {fields.map((field) => {
        if (overrides && overrides[field.id]) {
          return overrides[field.id]({
            field,
            setFieldValue: setValue,
            values,
            formState,
          });
        } else {
          switch (field.type) {
            case 'text_long':
              return (
                <div key={field.id}>
                  <TextLongWidget entityField={field} fieldRules={fieldRules} />
                  <Spacing size="medium" />
                </div>
              );
            case 'string_long':
              return (
                <div key={field.id}>
                  <StringLongWidget
                    entityField={field}
                    fieldRules={fieldRules}
                  />
                  <Spacing size="medium" />
                </div>
              );
            case 'string':
            case 'link':
            case 'telephone':
              return field.multiple ? (
                <div key={field.id}>
                  <StringMultipleWidget
                    {...(field.type === 'telephone' ? { type: 'tel' } : {})}
                    entityField={field}
                    fieldRules={fieldRules}
                  />
                  <Spacing size="medium" />
                </div>
              ) : (
                <div key={field.id}>
                  <StringWidget
                    {...(field.type === 'telephone' ? { type: 'tel' } : {})}
                    entityField={field}
                    fieldRules={fieldRules}
                  />
                  <Spacing size="medium" />
                </div>
              );
            case 'email':
              return (
                <div key={field.id}>
                  <StringWidget
                    type="email"
                    entityField={field}
                    fieldRules={fieldRules}
                  />
                  <Spacing size="medium" />
                </div>
              );
            case 'password':
              return (
                <div key={field.id}>
                  <StringWidget
                    type="password"
                    entityField={field}
                    fieldRules={fieldRules}
                  />
                  <Spacing size="medium" />
                </div>
              );
            case 'integer':
              return (
                field.id !== 'id' && (
                  <div key={field.id}>
                    <StringWidget
                      type="number"
                      entityField={field}
                      fieldRules={fieldRules}
                    />
                    <Spacing size="medium" />
                  </div>
                )
              );
            case 'datetime':
              return (
                <div key={field.id}>
                  <DateSingleWidget
                    entityField={field}
                    fieldRules={fieldRules}
                  />
                  <Spacing size="medium" />
                </div>
              );
            case 'daterange':
              return field.multiple ? (
                <div key={field.id}>
                  <DateRangeMultipleWidget
                    entityField={field}
                    fieldRules={fieldRules}
                  />
                  <Spacing size="medium" />
                </div>
              ) : (
                <div key={field.id}>
                  <DateRangeWidget
                    entityField={field}
                    fieldRules={fieldRules}
                  />
                  <Spacing size="medium" />
                </div>
              );
            case 'list_string':
            case 'list_integer':
              return (
                <div key={field.id}>
                  <ListStringWidget
                    entityField={field}
                    fieldRules={fieldRules}
                  />
                  <Spacing size="medium" />
                </div>
              );
            case 'address':
              return (
                <div key={field.id}>
                  <AddressWidget entityField={field} fieldRules={fieldRules} />
                </div>
              );
            case 'entity_reference':
              const target = getTargetType(field);
              if (target === 'taxonomy_term') {
                if (field.maxNumItems === 1) {
                  return (
                    <div key={field.id}>
                      <TaxonomyReferenceSingle
                        entityField={field}
                        fieldRules={fieldRules}
                      />
                      <Spacing size="medium" />
                    </div>
                  );
                } else {
                  return (
                    <div key={field.id}>
                      <TaxonomyReferenceMultiple
                        entityField={field}
                        fieldRules={fieldRules}
                      />
                      <Spacing size="medium" />
                    </div>
                  );
                }
              } else {
                return null;
              }
            case 'boolean':
              return (
                <div key={field.id}>
                  <BooleanWidget entityField={field} fieldRules={fieldRules} />
                  <Spacing size="medium" />
                </div>
              );
            case 'image':
              return (
                <div key={field.id}>
                  {field.isCroppedImage ? (
                    <ImageUploadCroppingWidget
                      entityField={field}
                      fieldRules={fieldRules}
                    />
                  ) : (
                    <ImageFileWidget
                      entityField={field}
                      fieldRules={fieldRules}
                    />
                  )}
                  <Spacing size="medium" />
                </div>
              );
            case 'file':
              return (
                <div key={field.id}>
                  <FileWidget
                    entityField={field}
                    fieldRules={fieldRules}
                    value={values[field.id]}
                  />
                  <Spacing size="medium" />
                </div>
              );
            case 'term_level':
              return (
                <div key={field.id}>
                  <TermLevelWidget
                    name={name}
                    bundle={bundle}
                    entityField={field}
                    fieldRules={fieldRules}
                  />
                  <Spacing size="medium" />
                </div>
              );
            case 'term_level_with_date':
              return (
                <div key={field.id}>
                  <TermLevelWithDateWidget
                    name={name}
                    bundle={bundle}
                    entityField={field}
                    fieldRules={fieldRules}
                  />
                  <Spacing size="medium" />
                </div>
              );
            case 'jobiqo_job_salary_range':
              return (
                <div key={field.id}>
                  <SalaryRangeWidget
                    entityField={field}
                    fieldRules={fieldRules}
                  />
                  <Spacing size="medium" />
                </div>
              );
            default:
              return (
                <AdditionalFieldItemWidgetsSlot {...field} {...fieldRules} />
              );
          }
        }
      })}
    </>
  );
};

export default FormItemsWidget;
